import type { Editor, PointerInfo, Tool, ToolId } from "../editor";
import { type Vec, boxFromPoints, dist } from "../geometry";
import { TransformSession, cursorForHandle, handleAt } from "./transform";
import { createTextAt } from "./create";
import { insideLasso } from "../elements";
import { toggleTask } from "../markdown";

/** Remembers the last tap so a second quick tap on the same spot becomes a double tap. */
export class TapTracker {
  private last: { t: number; p: Vec; id: string | null } | null = null;
  isDouble(p: Vec, id: string | null, screenTol = 24): boolean {
    const now = performance.now();
    const hit = !!this.last && now - this.last.t < 380 && dist(this.last.p, p) < screenTol && this.last.id === id;
    this.last = hit ? null : { t: now, p, id };
    return hit;
  }
}

export class SelectTool implements Tool {
  readonly id: ToolId = "select";
  private session: TransformSession | null = null;
  private marquee: { start: Vec; additive: boolean; base: string[] } | null = null;
  private downOn: string | null = null;
  private downWasSelected = false;
  private taps = new TapTracker();
  private cursorStyle = "default";
  private longPress: number | null = null;
  private downAt: Vec | null = null;
  private pendingDouble: { id: string | null; p: PointerInfo } | null = null;
  private downTarget: HTMLElement | null = null;

  constructor(protected editor: Editor) {}

  cursor(): string {
    return this.cursorStyle;
  }

  onDown(p: PointerInfo): void {
    const ed = this.editor;
    this.downOn = null;
    this.downAt = p.screen;
    const handle = handleAt(ed, p.screen, p.kind);
    if (handle) {
      const mode = handle === "rot" ? "rotate" : handle === "p0" || handle === "p1" || handle === "pm" ? "point" : "resize";
      this.session = new TransformSession(ed, mode, p, handle);
      return;
    }
    const hit = ed.hitAt(p.world, p.kind === "mouse" ? 6 : 12, { includeLocked: true });
    if (hit && this.taps.isDouble(p.screen, hit.id)) {
      this.pendingDouble = { id: hit.id, p };
      return;
    }
    if (hit) {
      if (ed.ui.editingId && ed.ui.editingId !== hit.id) ed.stopEditing();
      this.downOn = hit.id;
      this.downWasSelected = ed.ui.selection.includes(hit.id);
      this.downTarget = hit.type === "card" ? domAt(ed, p) : null;
      if (p.shift || p.meta) {
        ed.select([hit.id], "toggle");
        return;
      }
      if (!this.downWasSelected) ed.select([hit.id]);
      if (hit.locked) return;
      this.session = new TransformSession(ed, "move", p);
      this.startLongPress(p, hit.id);
      return;
    }
    if (this.taps.isDouble(p.screen, null) && !ed.ui.readOnly) {
      ed.clearSelection();
      this.pendingDouble = { id: null, p };
      return;
    }
    ed.stopEditing();
    const additive = p.shift || p.meta;
    if (!additive) ed.clearSelection();
    this.marquee = { start: p.world, additive, base: additive ? ed.ui.selection : [] };
  }

  onMove(p: PointerInfo): void {
    const ed = this.editor;
    if (this.session) {
      if (this.longPress !== null && this.downAt && dist(p.screen, this.downAt) > 8) this.cancelLongPress();
      this.session.update(p);
      return;
    }
    if (this.marquee) {
      const box = boxFromPoints([this.marquee.start, p.world]);
      ed.setOverlay({ marquee: box });
      if (box.w * ed.camera.z > 3 || box.h * ed.camera.z > 3) {
        const ids = ed.elementsInBox(box).map((e) => e.id);
        ed.setUi({ selection: [...new Set([...this.marquee.base, ...ed.expandGroups(ids)])] });
      }
    }
  }

  onUp(): void {
    const ed = this.editor;
    this.cancelLongPress();
    if (this.pendingDouble) {
      const { id, p } = this.pendingDouble;
      this.pendingDouble = null;
      if (id) this.onDoubleTap(id, p);
      else createTextAt(ed, p.world);
      return;
    }
    if (this.session) {
      const moved = this.session.finish();
      this.session = null;
      // a click on an element of a multi-selection narrows the selection to it
      if (!moved && this.downOn && this.downWasSelected && ed.ui.selection.length > 1) ed.select([this.downOn]);
      if (!moved && this.downOn && this.downTarget) cardTap(ed, this.downOn, this.downTarget, this.downWasSelected);
    }
    this.downTarget = null;
    if (this.marquee) {
      ed.setOverlay({ marquee: null });
      this.marquee = null;
    }
    this.downOn = null;
  }

  onCancel(): void {
    this.cancelLongPress();
    this.pendingDouble = null;
    this.session?.cancel();
    this.session = null;
    if (this.marquee) this.editor.setOverlay({ marquee: null });
    this.marquee = null;
  }

  onTap(p: PointerInfo): void {
    // finger tap while the pencil owns drawing: select what's under the finger
    const ed = this.editor;
    const hit = ed.hitAt(p.world, 14, { includeLocked: true });
    if (hit && this.taps.isDouble(p.screen, hit.id)) {
      this.onDoubleTap(hit.id, p);
      return;
    }
    if (hit) {
      const was = ed.ui.selection.includes(hit.id);
      ed.select([hit.id]);
      if (hit.type === "card") {
        const t = domAt(ed, p);
        if (t) cardTap(ed, hit.id, t, was);
      }
    } else {
      ed.stopEditing();
      ed.clearSelection();
    }
  }

  onHover(p: PointerInfo | null): void {
    const ed = this.editor;
    if (!p) {
      ed.setUi({ hoverId: null });
      return;
    }
    const handle = handleAt(ed, p.screen, p.kind);
    if (handle) {
      this.cursorStyle = cursorForHandle(handle, ed.selectionFrame()?.rot ?? 0);
      ed.setUi({ hoverId: null });
    } else {
      const hit = ed.hitAt(p.world, 6);
      this.cursorStyle = hit ? "move" : "default";
      ed.setUi({ hoverId: hit?.id ?? null });
    }
    if (ed.viewport) ed.viewport.style.cursor = this.cursorStyle;
  }

  deactivate(): void {
    this.onCancel();
  }

  protected onDoubleTap(id: string, p: PointerInfo): void {
    const ed = this.editor;
    const el = ed.doc.get(id);
    if (!el) return;
    this.session?.cancel();
    this.session = null;
    if (el.groupId && ed.ui.enteredGroup !== el.groupId) {
      ed.setUi({ enteredGroup: el.groupId, selection: [id] });
      return;
    }
    if (el.type === "card" && el.md) {
      // double-tap on a [[link]] follows it instead of editing
      const link = (document.elementFromPoint(p.screen.x + (ed.viewport?.getBoundingClientRect().left ?? 0), p.screen.y + (ed.viewport?.getBoundingClientRect().top ?? 0)) as HTMLElement | null)?.closest("[data-link]");
      if (link) {
        ed.host.openBoardLink(link.getAttribute("data-link") || "");
        return;
      }
    }
    if (el.type === "sticker" || el.type === "ink") return;
    ed.startEditing(id);
  }

  private startLongPress(p: PointerInfo, id: string): void {
    this.cancelLongPress();
    if (p.kind === "mouse") return;
    const at = p.screen;
    this.longPress = window.setTimeout(() => {
      this.longPress = null;
      this.session?.cancel();
      this.session = null;
      this.editor.select([id]);
      this.editor.viewport?.dispatchEvent(new CustomEvent("sepia:contextmenu", { detail: { x: at.x, y: at.y } }));
    }, 600);
  }

  private cancelLongPress(): void {
    if (this.longPress !== null) clearTimeout(this.longPress);
    this.longPress = null;
  }
}

/** The rendered DOM node under a canvas point (cards render real HTML: checkboxes, links). */
function domAt(ed: Editor, p: PointerInfo): HTMLElement | null {
  const r = ed.viewport?.getBoundingClientRect();
  if (!r) return null;
  return document.elementFromPoint(p.screen.x + r.left, p.screen.y + r.top) as HTMLElement | null;
}

/** A tap on an index card: tick a checkbox at once; follow a link once the card is already selected. */
function cardTap(ed: Editor, id: string, target: HTMLElement, wasSelected: boolean): void {
  const task = target.closest("[data-task]");
  if (task && !ed.ui.readOnly) {
    const n = Number(task.getAttribute("data-task"));
    ed.update(id, (el) => (el.type === "card" ? { ...el, md: toggleTask(el.md, n) } : el));
    return;
  }
  if (!wasSelected) return;
  const wiki = target.closest("[data-link]");
  if (wiki) {
    ed.host.openBoardLink(wiki.getAttribute("data-link") || "");
    return;
  }
  const a = target.closest("a[data-href]");
  const href = a?.getAttribute("data-href");
  if (href && /^(https?:|mailto:)/i.test(href)) window.open(href, "_blank", "noopener");
}

/** Lasso: draw a loop around things to select them; drag inside the selection to move it. */
export class LassoTool implements Tool {
  readonly id: ToolId = "lasso";
  private session: TransformSession | null = null;
  private pts: Vec[] | null = null;

  constructor(private editor: Editor) {}

  cursor(): string {
    return "crosshair";
  }

  onDown(p: PointerInfo): void {
    const ed = this.editor;
    const handle = handleAt(ed, p.screen, p.kind);
    if (handle) {
      const mode = handle === "rot" ? "rotate" : handle === "p0" || handle === "p1" || handle === "pm" ? "point" : "resize";
      this.session = new TransformSession(ed, mode, p, handle);
      return;
    }
    const frame = ed.selectionFrame();
    if (frame && ed.ui.selection.length) {
      const b = ed.selectionBounds()!;
      if (p.world.x >= b.x && p.world.x <= b.x + b.w && p.world.y >= b.y && p.world.y <= b.y + b.h) {
        this.session = new TransformSession(ed, "move", p);
        return;
      }
    }
    ed.clearSelection();
    this.pts = [p.world];
  }

  onMove(p: PointerInfo, coalesced: PointerInfo[]): void {
    if (this.session) {
      this.session.update(p);
      return;
    }
    if (!this.pts) return;
    for (const c of coalesced) {
      const last = this.pts[this.pts.length - 1];
      if (dist(last, c.world) * this.editor.camera.z > 2) this.pts.push(c.world);
    }
    this.editor.setOverlay({ lasso: this.pts.slice() });
  }

  onUp(): void {
    const ed = this.editor;
    if (this.session) {
      this.session.finish();
      this.session = null;
      return;
    }
    if (this.pts && this.pts.length > 3) {
      const poly = this.pts;
      ed.select(ed.doc.all().filter((e) => ed.isEditable(e) && insideLasso(e, poly)).map((e) => e.id));
    }
    this.pts = null;
    ed.setOverlay({ lasso: null });
  }

  onCancel(): void {
    this.session?.cancel();
    this.session = null;
    this.pts = null;
    this.editor.setOverlay({ lasso: null });
  }

  onTap(p: PointerInfo): void {
    const hit = this.editor.hitAt(p.world, 14);
    if (hit) this.editor.select([hit.id]);
    else this.editor.clearSelection();
  }

  deactivate(): void {
    this.onCancel();
  }
}
